import axios from 'axios';
import authService from './authService';
import { getValidTenantId } from '../utils/tenantUtils';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Helper function to get headers with authentication and tenant ID
const getHeadersWithTenant = () => {
  const headers = authService.getAuthHeader();
  const tenantId = getValidTenantId({ useLocalStorage: true });
  
  if (tenantId) {
    headers['X-Tenant-ID'] = tenantId;
  }
  
  return headers;
};

// Safe API request function with better error handling
const safeApiRequest = async (method, url, data = null) => {
  try {
    const headers = getHeadersWithTenant();
    console.log(`Making ${method.toUpperCase()} request to: ${API_URL}${url}`);
    
    const config = { 
      headers,
      method,
      url: `${API_URL}${url}`
    };
    
    if (data && (method === 'post' || method === 'put' || method === 'patch')) {
      config.data = data;
    }
    
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.error(`API Request failed for ${url}:`, error);
    
    // If the endpoint returns 404 or is not yet implemented, throw a descriptive error
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || 'An error occurred';
      
      if (status === 404) {
        throw new Error(`The wallet service endpoint (${url}) was not found. The service may not be implemented yet.`);
      } else if (status === 501) {
        throw new Error(`The wallet service endpoint (${url}) is not implemented yet.`);
      } else {
        throw new Error(`${message} (Status: ${status})`);
      }
    }
    
    // For connection errors, be explicit
    if (error.message && (error.message.includes('Network Error') || error.message.includes('timeout'))) {
      throw new Error(`Network error connecting to the wallet service. Please check your connection and try again.`);
    }
    
    throw error.response?.data?.message || error.message || 'An unknown error occurred';
  }
};

const walletService = {
  // Get wallet information
  getWallet: async () => {
    return safeApiRequest('get', '/wallet');
  },
  
  // Get transaction history
  getTransactions: async (page = 1, limit = 10) => {
    return safeApiRequest('get', `/wallet/transactions?page=${page}&limit=${limit}`);
  },
  
  // Get transaction by ID
  getTransaction: async (transactionId) => {
    return safeApiRequest('get', `/wallet/transactions/${transactionId}`);
  },
  
  // Update wallet preferences (e.g., notifications)
  updatePreferences: async (preferences) => {
    return safeApiRequest('patch', '/wallet/preferences', preferences);
  },
  
  // Request a transfer or withdrawal
  requestWithdrawal: async (withdrawalData) => {
    return safeApiRequest('post', '/wallet/withdrawals', withdrawalData);
  },
  
  // Get financial summary
  getFinancialSummary: async (period = 'month') => {
    return safeApiRequest('get', `/wallet/summary?period=${period}`);
  }
};

export default walletService;
